import { dayName } from "./format";
import type { EventView, InboxView, ObservedView, PersonCard } from "./types";

/**
 * Email presentation helpers (21 §3–6). Pure functions, tested in test/email.test.ts.
 * Nothing here judges a choice: kinds, deadlines and tones are described, never rated.
 */

export type EmailKind = NonNullable<EventView["email"]>["kind"];
export type Tone = "brief" | "warm" | "formal" | "firm";

/** Keeps the protected block against a raid (engine HOLD_OPTION; the web imports engine types only). */
export const HOLD_OPTION = "__hold";

/** The kind of email in words, with a decorative glyph. The word is always shown (never colour or glyph alone). */
export function emailKindInfo(kind: EmailKind | undefined): { glyph: string; word: string } {
  switch (kind) {
    case "noise": return { glyph: "·", word: "Noise" };
    case "fyi": return { glyph: "i", word: "FYI" };
    case "request": return { glyph: "?", word: "Request" };
    case "hot": return { glyph: "!", word: "Hot" };
    default: return { glyph: "✉", word: "Email" };
  }
}

/** Does this kind expect a reply? Filing one of these means not replying. */
export function expectsReply(kind: EmailKind | undefined): boolean {
  return kind === "request" || kind === "hot";
}

export type EmailFilter = "all" | "needs_reply" | "fyi" | "noise" | "partners";

export const EMAIL_FILTERS: { id: EmailFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "needs_reply", label: "Needs a reply" },
  { id: "fyi", label: "FYI" },
  { id: "noise", label: "Noise" },
  { id: "partners", label: "From partners" },
];

const PARTNER_GRADES = new Set(["equity_partner", "salaried_partner"]);

export function isPartner(grade: string | undefined): boolean {
  return grade !== undefined && PARTNER_GRADES.has(grade);
}

export function filterEmails(emails: EventView[], filter: EmailFilter): EventView[] {
  switch (filter) {
    case "all": return emails;
    case "needs_reply": return emails.filter((e) => expectsReply(e.email?.kind));
    case "fyi": return emails.filter((e) => e.email?.kind === "fyi");
    case "noise": return emails.filter((e) => e.email?.kind === "noise");
    case "partners": return emails.filter((e) => isPartner(e.bringer?.grade));
  }
}

export function unreadCount(emails: EventView[]): number {
  return emails.filter((e) => e.email && !e.email.read).length;
}

/** The nav label: "Inbox (12)" when anything is unread. */
export function inboxNavLabel(emails: EventView[]): { label: string; aria: string } {
  const n = unreadCount(emails);
  return n > 0
    ? { label: `Inbox (${n})`, aria: `Inbox, ${n} unread` }
    : { label: "Inbox", aria: "Inbox" };
}

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
type CalendarPoint = { week: number; day: string; half: "am" | "pm" };

/** Comparable position of a half-day (week, day, half). */
export function pointIndex(p: CalendarPoint): number {
  const d = DAY_ORDER.indexOf(p.day);
  return p.week * 100 + (d < 0 ? 0 : d) * 2 + (p.half === "pm" ? 1 : 0);
}

/**
 * When a reply is expected, relative to now: "Reply by this afternoon", "Reply by Wed pm",
 * "Reply by next Mon am", "Reply by Thu am, week 6". Null when there is no deadline.
 */
export function respondByLabel(respondBy: CalendarPoint | null | undefined, now: Pick<ObservedView["clock"], "week" | "day" | "half">): string | null {
  if (!respondBy) return null;
  const short = dayName(respondBy.day).slice(0, 3);
  if (respondBy.week === now.week && respondBy.day === now.day) {
    return respondBy.half === "am" ? "Reply by this morning" : "Reply by this afternoon";
  }
  const when = `${short} ${respondBy.half}`;
  if (respondBy.week === now.week) return `Reply by ${when}`;
  if (respondBy.week === now.week + 1) return `Reply by next ${when}`;
  return `Reply by ${when}, week ${respondBy.week}`;
}

export type EmailSort = "newest" | "reply_by";

/** Newest first (the server's order) or soonest reply-by first; emails without a deadline go last. */
export function sortEmails(emails: EventView[], sort: EmailSort): EventView[] {
  if (sort === "newest") return emails;
  return emails
    .map((e, i) => ({ e, i, at: e.email?.respondBy ? pointIndex(e.email.respondBy) : Number.POSITIVE_INFINITY }))
    .sort((a, b) => a.at - b.at || a.i - b.i)
    .map((x) => x.e);
}

/** Reading time before opening: "~2 min to read". */
export function readTimeLabel(minutes: number | undefined): string {
  const m = Math.max(1, Math.round(minutes ?? 1));
  return `~${m} min to read`;
}

/** A single-line preview of the message body. */
export function previewText(text: string, max = 90): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
}

/** How each tone may come across (21 §4b). Descriptions of risk, never a recommendation. */
export const TONES: Record<Tone, { label: string; description: string }> = {
  brief: { label: "Brief", description: "Quick and efficient. Someone stretched, junior or wary of you may read it as dismissive." },
  warm: { label: "Warm", description: "Personal and kind. It can read as soft when there is a standard to hold." },
  formal: { label: "Formal", description: "Clear and on the record. It can read as cold, or as an escalation." },
  firm: { label: "Firm", description: "Holds a line. A partner may read it as a challenge." },
};

const TONE_ORDER: Tone[] = ["brief", "warm", "formal", "firm"];

/** Only the tones offered, in a stable order, with their words. Unknown tones are dropped. */
export function toneChoices(tones: readonly string[] | undefined): { id: Tone; label: string; description: string }[] {
  if (!tones?.length) return [];
  return TONE_ORDER.filter((t) => tones.includes(t)).map((t) => ({ id: t, ...TONES[t] }));
}

export function toneLabel(tone: string | null | undefined): string | null {
  if (!tone) return null;
  return (TONES as Record<string, { label: string }>)[tone]?.label ?? tone.charAt(0).toUpperCase() + tone.slice(1);
}

/** How a message in the history was dealt with, in words. Null for non-email conversations. */
export function outcomeLabel(item: Pick<InboxView, "outcome" | "auto"> & { reply?: string }): string | null {
  switch (item.outcome) {
    case "replied": return item.auto ? "Handled the usual way" : "Replied";
    case "filed": return "Filed";
    case "no_reply": return "No reply";
    case "forwarded": { const f = forwardedDetails({ outcome: item.outcome, reply: item.reply ?? "" }); return `Forwarded to ${f?.to ?? "someone"}`; }
    default: return null;
  }
}

/** Noise emails the bulk action would file, oldest first so the list order settles predictably. */
export function noiseToArchive(emails: EventView[]): string[] {
  return emails.filter((e) => e.email?.kind === "noise").map((e) => e.instanceId).reverse();
}

/* --- copying in, reply-all and forwarding (21 §4d) ------------------------------------------------ */

type Named = Pick<PersonCard, "name">;

/** First names joined the way people say them: "Rachel", "Rachel and Helen", "Rachel, Helen and Tom". */
export function namesInWords(people: readonly Named[]): string {
  const names = people.map((p) => p.name.split(" ")[0] || p.name);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Who else a reply-all reaches, in words: "Reply all (also goes to Rachel and Helen)". Null if not reply-all or nobody is copied in. */
export function replyAllLabel(option: { replyAll?: boolean }, cc: readonly Named[] | undefined): string | null {
  if (!option.replyAll || !cc?.length) return null;
  return `Reply all (also goes to ${namesInWords(cc)})`;
}

/** Tones for a reply, noting when everyone copied in will read it too. Descriptions of risk, never a recommendation. */
export function replyToneChoices(tones: readonly string[] | undefined, cc: readonly Named[] | undefined, replyAll: boolean | undefined) {
  const choices = toneChoices(tones);
  if (!replyAll || !cc?.length) return choices;
  const who = cc.length === 1 ? namesInWords(cc) : `everyone copied in (${namesInWords(cc)})`;
  return choices.map((c) => ({ ...c, description: `${c.description} ${capitalise(who)} will read it too.` }));
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** People an email can be forwarded to: everyone who is in, except you. */
export function forwardRecipients<P extends Pick<PersonCard, "id" | "availability">>(people: readonly P[], youId: string): P[] {
  // Someone serving notice is still at work and can take it; someone away cannot.
  return people.filter((p) => p.id !== youId && p.availability !== "away");
}

export const FORWARD_NOTE_MAX = 300;

/** What's wrong with a forward before it can be sent, in words; null when it can go. */
export function forwardError(personId: string, note: string): string | null {
  if (!personId) return "Choose who to forward it to.";
  if (note.length > FORWARD_NOTE_MAX) return `Keep the note to ${FORWARD_NOTE_MAX} characters or fewer (it is ${note.length}).`;
  return null;
}

/** The characters left for a forwarding note, in words. */
export function noteCountLabel(note: string): string {
  const left = FORWARD_NOTE_MAX - note.length;
  return left >= 0 ? `${left} of ${FORWARD_NOTE_MAX} characters left` : `${-left} characters over the limit of ${FORWARD_NOTE_MAX}`;
}

/** Who a forwarded email went to, and the note sent with it, from the history line the engine records. */
export function forwardedDetails(item: Pick<InboxView, "outcome" | "reply">): { to: string; note: string | null } | null {
  if (item.outcome !== "forwarded") return null;
  const m = /^Forwarded to ([^:]+?)(?::\s*([\s\S]*))?$/.exec(item.reply.trim());
  if (!m) return { to: "someone", note: null };
  return { to: m[1]!, note: m[2]?.trim() || null };
}
